import { Component, computed, ElementRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NgComponentOutlet, NgStyle } from '@angular/common';
import { cloneDeep, merge } from 'lodash-es';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { DropdownService } from './dropdown.service';
import { DropdownComponent } from './dropdown.component';
import { defaultDropdownOptions, DropdownContent, DropdownInputs, DropdownOptions } from './model';

@Component({
  selector: 'app-dropdown-layout',
  imports: [NgStyle, NgClickOutsideDirective, NgComponentOutlet],
  templateUrl: './dropdown-layout.component.html',
  styles: `
    :host { display: contents; }
    .dropdown-position { position: absolute; z-index: 9999; }
  `
})
export class DropdownLayoutComponent implements OnInit, OnDestroy {

  dropdownClass = DropdownComponent
  dropdownContent = signal<DropdownContent|undefined>(undefined)
  positionHost = signal<HTMLElement|undefined>(undefined)
  options = signal<DropdownOptions|undefined>(undefined)
  private positionVersion = signal(0)

  protected inputs = computed(() => {
    const content = this.dropdownContent()
    const options = this.options()
    if (content && options) {
      const inputs: DropdownInputs = {content, options}
      return inputs as unknown as Record<string, unknown>
    }
    return undefined
  })

  protected style = computed(() => {
    this.positionVersion()
    const positionHost = this.positionHost()
    const options = this.options()
    if (positionHost && options) {
      return this.calcPosition(positionHost, options)
    }
    return undefined
  })

  private dropdown = inject(DropdownService)

  ngOnInit(): void {
    this.dropdown.registerLayout(this)
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.updatePositionOnScroll, true)
  }

  create(content: DropdownContent, positionHost: HTMLElement|ElementRef<HTMLElement>, options?: Partial<DropdownOptions>) {
    this.destroy()
    this.dropdownContent.set(content)
    this.positionHost.set(positionHost instanceof ElementRef ? positionHost.nativeElement : positionHost)
    this.options.set(merge(cloneDeep(defaultDropdownOptions), options))
    document.addEventListener('scroll', this.updatePositionOnScroll, true)
  }

  destroy() {
    document.removeEventListener('scroll', this.updatePositionOnScroll, true)
    this.dropdownContent.set(undefined)
  }

  updatePosition() {
    this.positionVersion.update(version => version + 1)
  }

  private updatePositionOnScroll = (): void => {
    this.updatePosition()
  }

  protected calcPosition(positionHost: HTMLElement, options: DropdownOptions): Record<string, string> {
    const bounds  = positionHost.getBoundingClientRect()
    const scrollY = window.scrollY || document.documentElement.scrollTop
    const scrollX = window.scrollX || document.documentElement.scrollLeft

    const res: Record<string, string> = {
      'width': `${bounds.width}px`,
      'visibility': this.isPositionHostVisible(positionHost, bounds) ? 'visible' : 'hidden',
    }

    switch (options.positionY) {
      case 'bottom':
        res['top'] = `${bounds.top + bounds.height + scrollY + options.offset}px`
        break
      case 'top':
        res['bottom'] = `${bounds.top + scrollY - options.offset}px`
        break
    }
    switch (options.positionX) {
      case 'left':
        res['left'] = `${bounds.left + scrollX}px`
        break;
      case 'right':
        res['right'] = `${document.documentElement.scrollWidth - bounds.right - scrollX}px`
        break;
    }

    return res;
  }

  private isPositionHostVisible(positionHost: HTMLElement, bounds: DOMRect): boolean {
    if (bounds.bottom <= 0 || bounds.top >= window.innerHeight || bounds.right <= 0 || bounds.left >= window.innerWidth) {
      return false
    }

    const pointX = Math.min(Math.max(bounds.left + bounds.width / 2, 0), window.innerWidth - 1)
    const pointY = Math.min(Math.max(bounds.top + bounds.height / 2, 0), window.innerHeight - 1)
    const elementAtPoint = document.elementFromPoint(pointX, pointY)

    return elementAtPoint !== null && (elementAtPoint === positionHost || positionHost.contains(elementAtPoint))
  }

}
